import { format } from 'date-fns'
import { useIotSensorHistories } from 'modules/iot/api/queries'
import {
  classifyZone,
  formatLastUpdate,
  getSensorKeys,
  getSensorMeta,
  getSensorValueStatus,
  toThreshold,
} from 'modules/iot/api/selector'
import { IotDeviceApi, IotHistoryParams } from 'modules/iot/api/types'
import SensorChart from 'modules/iot/components/SensorChart'
import TimeRangeSelector, {
  CHIP_GROUPS,
  INTERVAL_BY_UNIT,
  TimeRangeSelectorValue,
  relativeRangeToMs,
} from 'modules/iot/components/TimeRangeSelector'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PATH_DASHBOARD } from 'routes/paths'

import {
  Box,
  Button,
  Card,
  Grid,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'

import { Icon } from '@iconify/react'

const SENSOR_ICONS: Record<string, string> = {
  do: '/static/iot/do.png',
  ph: '/static/iot/ph.png',
  temperature: '/static/iot/temperature.png',
  ammonia: '/static/iot/ammonia.png',
  turbidity: '/static/iot/turbidity.png',
  salinity: '/static/iot/iot-device.png',
}

const LEGEND_KEYS = [
  { labelKey: 'status.normal', color: 'primary' as const },
  { labelKey: 'status.warning', color: 'warning' as const },
  { labelKey: 'status.critical', color: 'error' as const },
]

const buildDefaultSelector = (): TimeRangeSelectorValue => ({
  unit: 'minute',
  relativeRange: CHIP_GROUPS.minute[0].value,
  mode: 'relative',
  startDate: null,
  startTime: null,
  endDate: null,
  endTime: null,
})

interface WaterIotTabProps {
  device: IotDeviceApi
}

export default function WaterIotTab({ device }: WaterIotTabProps) {
  const { t } = useTranslation('iot')
  const theme = useTheme()
  const navigate = useNavigate()

  const [selector, setSelector] = useState<TimeRangeSelectorValue>(buildDefaultSelector)

  const sensors = useMemo(() => getSensorKeys(device), [device])

  const visibleSensors = useMemo(
    () =>
      sensors
        .map((sensor) => {
          const value = device.measurements?.[sensor]
          return {
            sensor,
            value,
            valueStatus: getSensorValueStatus(value, device.device_connection_status),
          }
        })
        .filter((s) => s.valueStatus !== 'no_data'),
    [sensors, device]
  )

  const historyParams = useMemo<Omit<IotHistoryParams, 'sensor'>>(() => {
    const interval = INTERVAL_BY_UNIT[selector.unit]
    const defaultEndMs = Date.now()

    if (
      selector.mode === 'absolute' &&
      selector.startDate &&
      selector.startTime &&
      selector.endDate &&
      selector.endTime
    ) {
      return {
        timeFilterType: 'absolute',
        absoluteTimeStart: new Date(
          `${selector.startDate}T${selector.startTime}:00+07:00`
        ).toISOString(),
        absoluteTimeEnd: new Date(`${selector.endDate}T${selector.endTime}:00+07:00`).toISOString(),
        interval,
      }
    }

    return {
      timeFilterType: 'absolute',
      absoluteTimeStart: new Date(
        defaultEndMs - relativeRangeToMs(selector.relativeRange)
      ).toISOString(),
      absoluteTimeEnd: new Date(defaultEndMs).toISOString(),
      interval,
    }
  }, [selector])

  const histories = useIotSensorHistories(device.id, sensors, historyParams)

  const { startTs, endTs } = useMemo(
    () => ({
      startTs: historyParams.absoluteTimeStart
        ? new Date(historyParams.absoluteTimeStart).getTime()
        : Date.now(),
      endTs: historyParams.absoluteTimeEnd
        ? new Date(historyParams.absoluteTimeEnd).getTime()
        : Date.now(),
    }),
    [historyParams]
  )

  const lastUpdateTs = device.device_timestamp ? new Date(device.device_timestamp).getTime() : null

  return (
    <Stack spacing={2}>
      {/* Device header */}
      <Card sx={{ borderRadius: 3, p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" color="text.secondary">
              {[
                `${t('farm.label')}: ${device.farm_name ?? '-'}`,
                `${t('pond.label')}: ${device.pond_name ?? '-'}`,
              ].join(' | ')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {device.device_name}
              </Typography>
              <Icon
                icon={
                  device.device_connection_status === 'ONLINE' ? 'tabler:wifi' : 'tabler:wifi-off'
                }
                width={18}
                height={18}
                color={
                  device.device_connection_status === 'ONLINE'
                    ? theme.palette.success.main
                    : theme.palette.text.secondary
                }
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              ID: {device.external_device_id}{' '}
              {lastUpdateTs
                ? t('device.updated_at', { time: formatLastUpdate(lastUpdateTs) })
                : t('device.updated_at', { time: '-' })}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            endIcon={<Icon icon="mdi:chevron-right" width={16} />}
            onClick={() => navigate(PATH_DASHBOARD.iot.detail(device.id))}
            sx={{ flexShrink: 0 }}
          >
            {t('action.go_device')}
          </Button>
        </Stack>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {visibleSensors.map(({ sensor, value, valueStatus }) => {
            const meta = getSensorMeta(sensor)
            const iconSrc = SENSOR_ICONS[meta.iconKey]
            const th = toThreshold(device.thresholds?.[sensor], value)
            const zone = classifyZone(value, th)
            const zoneColor =
              zone === 'critical'
                ? theme.palette.error.main
                : zone === 'warning'
                ? theme.palette.warning.main
                : theme.palette.primary.main

            return (
              <Grid key={sensor} item xs={12} sm={4}>
                <Card
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 'none',
                    bgcolor: alpha(zoneColor, 0.06),
                    border: `1px solid ${alpha(zoneColor, 0.18)}`,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      {iconSrc && <img src={iconSrc} width={18} height={18} alt={sensor} />}
                      <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {t(meta.label)}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {meta.unit}
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      mt: 1.5,
                      textAlign: 'right',
                      fontSize: 32,
                      fontWeight: 700,
                      color: valueStatus === 'value' ? zoneColor : 'text.secondary',
                      lineHeight: 1,
                    }}
                  >
                    {valueStatus === 'value' ? value!.toFixed(2) : '-'}
                  </Typography>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      </Card>

      {/* Time range selector */}
      <Card sx={{ borderRadius: 3, p: { xs: 2, sm: 3 } }}>
        <TimeRangeSelector
          value={selector}
          onChange={setSelector}
          hasCycle={!!(device.cycle_id && device.cycle_start_date)}
        />
      </Card>

      {/* Charts */}
      <Card sx={{ borderRadius: 3, p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography sx={{ fontWeight: 600 }}>{t('chart.data')}</Typography>
          <Stack direction="row" spacing={1.5}>
            {LEGEND_KEYS.map((l) => (
              <Stack key={l.labelKey} direction="row" spacing={0.5} alignItems="center">
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: theme.palette[l.color].main,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t(l.labelKey)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {t('time.period', {
            start: format(new Date(startTs), 'dd/MM/yyyy HH:mm'),
            end: format(new Date(endTs), 'dd/MM/yyyy HH:mm'),
          })}
        </Typography>

        <Stack spacing={3}>
          {sensors.map((sensor, i) => {
            const query = histories[i]
            const current = device.measurements?.[sensor]
            const threshold = device.thresholds?.[sensor]

            if (query?.isLoading) {
              return (
                <Skeleton key={sensor} variant="rounded" height={240} sx={{ borderRadius: 2 }} />
              )
            }
            if (!query?.data) {
              return (
                <Box
                  key={sensor}
                  sx={{
                    height: 240,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('sensor.no_data_named', { sensor: t(getSensorMeta(sensor).label) })}
                  </Typography>
                </Box>
              )
            }
            return (
              <SensorChart
                key={sensor}
                sensor={sensor}
                history={query.data}
                threshold={threshold}
                current={current}
                unit={selector.unit}
                rangeStart={startTs}
                rangeEnd={endTs}
              />
            )
          })}
        </Stack>
      </Card>
    </Stack>
  )
}
